import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  CycleSummary,
  Enrollment,
  EnrollmentDetail,
  StartCheckoutInput,
  StartCheckoutResponse,
  startEnrollmentCheckout as apiStartCheckout,
  confirmEnrollmentCheckout as apiConfirmCheckout,
  getEnrollmentDetail as apiGetEnrollmentDetail,
  getMyEnrollments as apiGetMyEnrollments,
  previewCycle as apiPreviewCycle,
} from '../api/enrollments';

interface EnrollmentsState {
  cyclePreview: CycleSummary | null;
  selectedEnrollment: EnrollmentDetail | null;
  myEnrollments: Enrollment[];
  loading: boolean;
  submitting: boolean;
  error: string | null;
  conflictEnrollmentId?: string;
}

const initialState: EnrollmentsState = {
  cyclePreview: null,
  selectedEnrollment: null,
  myEnrollments: [],
  loading: false,
  submitting: false,
  error: null,
};

export const fetchCyclePreview = createAsyncThunk(
  'enrollments/preview',
  async (args: { courseId: string; childId: string }, { rejectWithValue }) => {
    try {
      const result = await apiPreviewCycle(args.courseId, args.childId);
      return result.cycle;
    } catch (error: any) {
      return rejectWithValue(error.message as string);
    }
  },
);

/** What the parent reads when the course was re-priced under them (PRT-BR-P07-05). */
export const TERMS_CHANGED_MESSAGE =
  'El precio de este curso cambió desde que lo revisaste. Revisa el resumen actualizado antes de continuar.';

interface StartCheckoutRejection {
  message: string;
  conflict?: string;
  /** Present when the server refused because the terms moved: the current ones. */
  currentCycle?: CycleSummary;
}

export const startCheckout = createAsyncThunk(
  'enrollments/startCheckout',
  async (args: StartCheckoutInput, { rejectWithValue }) => {
    try {
      return await apiStartCheckout(args);
    } catch (error: any) {
      // ApiError carries the parsed body. This read `error.response.data`
      // until now — an axios shape on a fetch client — so the existing
      // enrollment's id never reached the screen that links to it.
      const body = (error?.body ?? {}) as Record<string, any>;
      if (body.code === 'RECORDING_CONSENT_CHANGED') {
        return rejectWithValue({
          message: 'El aviso de grabación cambió. Léelo de nuevo y confirma.',
        } as StartCheckoutRejection);
      }
      if (body.code === 'TERMS_CHANGED') {
        return rejectWithValue({
          message: TERMS_CHANGED_MESSAGE,
          currentCycle: body.cycle as CycleSummary,
        } as StartCheckoutRejection);
      }
      return rejectWithValue({
        message: error.message as string,
        conflict: body.existingEnrollmentId as string | undefined,
      } as StartCheckoutRejection);
    }
  },
);

export const confirmCheckout = createAsyncThunk(
  'enrollments/confirmCheckout',
  async (args: { enrollmentId: string; sessionId: string }, { rejectWithValue }) => {
    try {
      return await apiConfirmCheckout(args.enrollmentId, args.sessionId);
    } catch (error: any) {
      return rejectWithValue(error.message as string);
    }
  },
);

export const fetchMyEnrollments = createAsyncThunk(
  'enrollments/listMine',
  async (_: void, { rejectWithValue }) => {
    try {
      const result = await apiGetMyEnrollments();
      return result.enrollments;
    } catch (error: any) {
      return rejectWithValue(error.message as string);
    }
  },
);

export const fetchEnrollmentDetail = createAsyncThunk(
  'enrollments/detail',
  async (enrollmentId: string, { rejectWithValue }) => {
    try {
      return await apiGetEnrollmentDetail(enrollmentId);
    } catch (error: any) {
      return rejectWithValue(error.message as string);
    }
  },
);

const enrollmentsSlice = createSlice({
  name: 'enrollments',
  initialState,
  reducers: {
    clearCyclePreview: (state) => {
      state.cyclePreview = null;
    },
    clearSelectedEnrollment: (state) => {
      state.selectedEnrollment = null;
    },
    clearEnrollmentError: (state) => {
      state.error = null;
      state.conflictEnrollmentId = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCyclePreview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCyclePreview.fulfilled, (state, action: PayloadAction<CycleSummary>) => {
        state.loading = false;
        state.cyclePreview = action.payload;
      })
      .addCase(fetchCyclePreview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(startCheckout.pending, (state) => {
        state.submitting = true;
        state.error = null;
        state.conflictEnrollmentId = undefined;
      })
      .addCase(startCheckout.fulfilled, (state, _action: PayloadAction<StartCheckoutResponse>) => {
        // Stay "submitting" — the component redirects to Stripe on success.
        state.submitting = true;
      })
      .addCase(startCheckout.rejected, (state, action) => {
        state.submitting = false;
        const payload = action.payload as StartCheckoutRejection | string | undefined;
        if (typeof payload === 'string' || payload === undefined) {
          state.error = payload ?? action.error.message ?? null;
        } else {
          state.error = payload.message;
          state.conflictEnrollmentId = payload.conflict;
          // The summary on screen becomes the terms that would actually be
          // charged, so the next press confirms what the parent now reads.
          if (payload.currentCycle) state.cyclePreview = payload.currentCycle;
        }
      })
      .addCase(confirmCheckout.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(confirmCheckout.fulfilled, (state, action) => {
        state.submitting = false;
        state.selectedEnrollment = action.payload;
      })
      .addCase(confirmCheckout.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      })
      .addCase(fetchMyEnrollments.fulfilled, (state, action) => {
        state.myEnrollments = action.payload;
      })
      .addCase(fetchEnrollmentDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEnrollmentDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedEnrollment = action.payload;
      })
      .addCase(fetchEnrollmentDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearCyclePreview, clearSelectedEnrollment, clearEnrollmentError } =
  enrollmentsSlice.actions;
export default enrollmentsSlice.reducer;
